<template>
  <div class="timeline-sidebar-content">
    <el-select
      v-model="selectedAnchorId"
      class="gateways-list"
      :placeholder="$tc('placeholder.selectGateway', 1)"
      @change="getTimeline"
      style="width: 100%; margin-bottom: 24px"
      clearable
    >
      <el-option
        v-for="gateway in gateways"
        :key="gateway.anchorId"
        :label="gateway.label"
        :value="gateway.anchorId"
        class="gateway-item"
      >
        <span
          class="timeline-sidebar-content__circle-color"
          :style="{ backgroundColor: gateway.color }"
        ></span>
        {{ gateway.label }}
      </el-option>
    </el-select>
    <div class="timeline-date-time-options">
      <span class="timeline-date-time-options__title">{{
        $t("statsTimeline.dateTimeTitle")
      }}</span>
      <div class="timeline-date-time-options__inputs">
        <el-date-picker
          v-model="selectedDate"
          type="date"
          :placeholder="$t('placeholder.selectDate')"
          style="width: 100%"
          :picker-options="datePickerOptions"
          format="dd-MM-yyyy"
          @change="getTimeline"
        ></el-date-picker>
        <el-time-picker
          is-range
          v-model="selectedTimeRange"
          range-separator="-"
          :start-placeholder="$t('placeholder.startTime')"
          :end-placeholder="$t('placeholder.endTime')"
          style="width: 100%"
          @change="getTimeline"
        />
      </div>
      <p v-if="showTimeErrorMsg" class="error-message">
        {{ $t("statsTimeline.timeErrorMessage") }}
      </p>
    </div>
    <TimelinePlaybackSlider
      :slider-options="sliderOptions"
      :is-disabled="timelineData.length === 0"
      :reset-playback="timelineData.length === 0"
      @slider-changed="val => (sliderOptions.value = val)"
    />
    <TimelinePositionCards
      :timelinePositions="sortedTimelinePositions"
      :loadingTimeline="loadingTimeline"
      :showNoDataMsg="showNoDataMsg"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import positionsApi from "@/modules/position-logs/api/position-logs.api";
import moment from "moment-timezone";
import TimelinePlaybackSlider from "@/components/stats/TimelinePlaybackSlider.vue";
import TimelinePositionCards from "@/components/stats/TimelinePositionCards.vue";

export default {
  name: "StatsTimeline",
  components: {
    TimelinePlaybackSlider,
    TimelinePositionCards
  },
  data() {
    return {
      moment,
      isSidebarVisible: true,
      selectedAnchorId: null,
      selectedDate: new Date(),
      selectedTimeRange: [
        new Date(new Date().setHours(11, 0, 0, 0)),
        new Date(new Date().setHours(18, 59, 59, 999))
      ],
      sliderOptions: {
        value: 0,
        min: 0,
        max: 10,
        step: 1,
        marks: {},
        speed: 1000
      },
      datePickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now();
        }
      },
      timelineData: [],
      colors: [
        "#3d85c6",
        "#3d41c6",
        "#2FA4A1",
        "#F2D890",
        "#B5877F",
        "#CD5332"
      ],
      gatewayTimelinePosition: null,
      loadingTimeline: false,
      showTimeErrorMsg: ""
    };
  },
  computed: {
    ...mapState("user", {
      resources: "resources"
    }),
    ...mapState("clients", {
      clientData: "data"
    }),
    gateways() {
      const filteredGateways =
        this.resources?.data?.anchors?.filter(gateway => gateway.type === 0) ||
        [];
      return filteredGateways.length
        ? filteredGateways.map((gateway, index) => ({
            ...gateway,
            color: this.colors[index % this.colors.length]
          }))
        : [];
    },
    sortedTimelinePositions() {
      const marks = this.sliderOptions.marks;
      const value = this.sliderOptions.value;
      const currentEnterTs = marks[value]?.enterTs;

      const currentIndex = this.timelineData.findIndex(
        entry => entry.enterTs === currentEnterTs
      );

      if (currentIndex === -1 || !this.timelineData.length) {
        return [];
      }

      // Gets up to 3 previous positions (oldest first)
      const pastPositions = this.timelineData
        .slice(Math.max(currentIndex - 3, 0), currentIndex)
        .map(pos => ({ ...pos, past: true }));

      // Gets current position
      const currentPosition = {
        ...this.timelineData[currentIndex],
        current: true
      };

      // Gets next (future) position if exists
      const futurePosition =
        currentIndex + 1 < this.timelineData.length
          ? { ...this.timelineData[currentIndex + 1], future: true }
          : null;

      // Compose result in visual order: [future, current, ...past]
      const positions = [];
      if (futurePosition) positions.push(futurePosition);
      positions.push(currentPosition);
      positions.push(...pastPositions.reverse());

      return positions;
    },
    gatewaysMap() {
      const map = {};
      for (const gateway of this.gateways) {
        map[gateway.anchorId] = gateway;
      }
      return map;
    },
    showNoDataMsg() {
      if (
        !this.showTimeErrorMsg &&
        this.selectedAnchorId &&
        this.selectedDate &&
        this.selectedTimeRange?.[0] &&
        this.selectedTimeRange?.[1] &&
        this.timelineData.length === 0 &&
        this.loadingTimeline === false
      ) {
        return true;
      }
      return false;
    }
  },
  methods: {
    getDateTimeWithSelectedDate(time) {
      return moment(time).set({
        year: this.selectedDate.getFullYear(),
        month: this.selectedDate.getMonth(),
        date: this.selectedDate.getDate()
      });
    },
    updateGatewayTimelinePosition() {
      // Gets current enterTs from slider
      const currentMark = this.sliderOptions.marks[this.sliderOptions.value];
      const currentEnterTs = currentMark?.enterTs;

      // Builds a map of anchorId to timeline entry for current enterTs
      const timelineMap = {};
      if (currentEnterTs != null) {
        for (const entry of this.timelineData) {
          if (entry.enterTs === currentEnterTs) {
            timelineMap[entry.anchorId] = entry;
          }
        }
      }

      const gateway = this.gatewaysMap[this.selectedAnchorId];
      const timelineEntry =
        timelineMap[gateway.id] ||
        this.timelineData.find(
          e => e.anchorId === gateway.id && e.enterTs === currentEnterTs
        )?.[0];

      // Fallback to initial or empty
      const position = timelineEntry || {
        anchorId: gateway.id,
        anchorLabel: gateway.label,
        enterTs: null,
        x: null,
        y: null,
        color: gateway.color
      };

      // Gets the timeline entry for current enterTs, or fallback
      this.gatewayTimelinePosition = position;
    },
    isTimeInvalid() {
      if (this.selectedTimeRange?.[0] && this.selectedTimeRange?.[1]) {
        const diffMs = this.selectedTimeRange[1] - this.selectedTimeRange[0];
        const diffHours = diffMs / (1000 * 60 * 60);
        if (diffHours > 8) {
          return true;
        }
      }
      return false;
    },
    async getTimeline() {
      this.loadingTimeline = true;
      this.gatewayTimelinePosition = null;
      this.timelineData = [];
      this.showTimeErrorMsg = this.isTimeInvalid();

      if (
        this.showTimeErrorMsg ||
        !this.selectedAnchorId ||
        !this.selectedDate ||
        !this.selectedTimeRange?.[0] ||
        !this.selectedTimeRange?.[1]
      ) {
        this.loadingTimeline = false;
        return;
      }

      // Prepare timestamps
      const startDateTime = this.getDateTimeWithSelectedDate(
        this.selectedTimeRange[0]
      );
      const endDateTime = this.getDateTimeWithSelectedDate(
        this.selectedTimeRange[1]
      );
      const timestampStart = startDateTime.utc().unix();
      const timestampEnd = endDateTime.utc().unix();

      try {
        // Fetch timeline data
        const payload = {
          timestampStart,
          timestampEnd,
          anchorIds: [this.selectedAnchorId]
        };
        const res = await positionsApi.getAnchorPositionLogs(payload);

        // Process timeline data and assign colors
        const updatedTimeline = res.data;
        const anchorColorMap = {};
        const gatewayInfo = this.gateways.find(
          gateway => gateway.anchorId === this.selectedAnchorId
        );
        anchorColorMap[gatewayInfo.id] = gatewayInfo.color;

        this.timelineData = updatedTimeline.map(entry => ({
          ...entry,
          color: anchorColorMap[entry.anchorId] || "#000000"
        }));

        // Build sliderOptions marks based on unique enterTs
        const sliderOptionsMarks = {};
        let lastEnterTs = null;
        let markIndex = 0;
        for (const entry of this.timelineData) {
          if (entry.enterTs !== lastEnterTs) {
            const enterTime = moment
              .unix(entry.enterTs)
              .tz(this.clientData.timezone)
              .format("HH:mm:ss");
            sliderOptionsMarks[markIndex] = {
              label: enterTime,
              enterTs: entry.enterTs
            };
            lastEnterTs = entry.enterTs;
            markIndex++;
          }
        }

        // Update sliderOptions
        this.sliderOptions.min = 0;
        this.sliderOptions.max = markIndex > 0 ? markIndex - 1 : 1;
        this.sliderOptions.value = this.sliderOptions.min;
        this.sliderOptions.marks = sliderOptionsMarks;
      } catch (error) {
        console.error("Failed to fetch timeline data:", error);
      } finally {
        this.updateGatewayTimelinePosition();
        this.loadingTimeline = false;
      }
    }
  },
  watch: {
    "sliderOptions.value"() {
      this.updateGatewayTimelinePosition();
    },
    gatewayTimelinePosition() {
      this.$emit("update-gateway-position", this.gatewayTimelinePosition);
    },
    sortedTimelinePositions: {
      handler() {
        this.$emit(
          "update-segments",
          this.sortedTimelinePositions.toReversed()
        );
      },
      deep: true
    }
  },
  mounted() {
    if (this.gateways.length > 0) {
      this.selectedAnchorId = this.gateways[0].anchorId;
      this.getTimeline();
    }
  }
};
</script>

<style lang="scss">
.timeline-sidebar-content {
  flex: 1 1 0%;

  .header-title {
    margin-bottom: 16px;
    text-align: left;
  }
  .gateways-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
  }
  .timeline-date-time-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
    &__title {
      font-weight: 600;
      font-size: 14px;
      color: #303133;
      text-align: left;
      margin-bottom: 8px;
    }
    &__inputs {
      display: flex;
      gap: 8px;
    }

    .error-message {
      color: #f56c6c;
      font-size: 12px;
      margin-top: 4px;
    }
  }
  &__circle-color {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 8px;
  }
}
.el-checkbox {
  margin-right: 0;
}
</style>
